import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Spacer } from 'app/layouts/generic';
import { Button } from 'app/shared/button';
import { DetailItem } from 'app/shared/info-section/item';
import { SimpleInput } from 'app/shared/input';
import { TextArea } from 'app/shared/input/textarea';
import { MultiSelect } from 'app/shared/select';
import { useTenantService } from 'hooks/api/iam/tenant';
import { useIssueReportService } from 'hooks/api/agents/issue-report';
import { assigneeFromUser } from './context';
import styles from '../issues.module.css';

export const AddTenantToIssueDialog = ({ issue, onAdded = () => {}, closeModal = () => {} }) => {
  const user_data = useSelector((store) => store.user_data);
  const { fetchTenants } = useTenantService();
  const { createIssueReport } = useIssueReportService();

  const [tenants, setTenants] = useState([]);
  const [tenant_option, setTenantOption] = useState(null);
  const [reporter_name, setReporterName] = useState('');
  const [reporter_email, setReporterEmail] = useState('');
  const [details, setDetails] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchTenants({ query_string: 'population=200' }).then(({ tenants: fetched }) =>
      setTenants(fetched || [])
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tenant_options = useMemo(
    () =>
      tenants.map((tenant) => ({
        value: String(tenant.id || tenant._id),
        label: tenant.name || tenant.emails?.[0] || tenant.email || String(tenant.id),
        tenant
      })),
    [tenants]
  );

  const assignee = useMemo(() => assigneeFromUser(user_data), [user_data]);
  const can_submit = !submitting && !!tenant_option;

  const handleSubmit = async () => {
    if (!can_submit) return;
    const tenant = tenant_option.tenant;
    setSubmitting(true);
    const { report } = await createIssueReport({
      issue_id: issue.id || issue._id,
      tenant: { id: tenant.id || tenant._id, name: tenant.name || '' },
      reporter:
        reporter_name.trim() || reporter_email.trim()
          ? { name: reporter_name.trim(), email: reporter_email.trim() }
          : undefined,
      details: details.trim() || undefined,
      assignee
    });
    setSubmitting(false);
    if (report) {
      onAdded(report);
      closeModal();
    }
  };

  return (
    <div className={styles.form}>
      <p className={styles.dialogSubtitle}>
        Link <strong>{issue.title}</strong> to a tenant that reported it.
      </p>

      <DetailItem title="Tenant" required>
        <MultiSelect
          options={tenant_options}
          value={tenant_option}
          onChange={setTenantOption}
          isMulti={false}
          isClearable
          placeholder="Search tenants…"
          menuPortalTarget={document.body}
        />
      </DetailItem>

      <Spacer multiple={2} />

      <DetailItem title="Reporter name">
        <SimpleInput value={reporter_name} onInput={setReporterName} placeholder="Ada Obi" />
      </DetailItem>

      <Spacer multiple={2} />

      <DetailItem title="Reporter email">
        <SimpleInput value={reporter_email} onInput={setReporterEmail} placeholder="ada@acme.co" />
      </DetailItem>

      <Spacer multiple={2} />

      <DetailItem title="Details">
        <TextArea
          rows={3}
          value={details}
          onInput={setDetails}
          placeholder="What did they report?"
        />
      </DetailItem>

      {assignee?.email && (
        <p className={styles.assigneeHint}>
          Assigned to{' '}
          {[assignee.firstname, assignee.lastname].filter(Boolean).join(' ') || assignee.email}
        </p>
      )}

      <Spacer multiple={3} />

      <div className={styles.dialogActions}>
        <Button text="Cancel" type="secondary" onClick={closeModal} />
        <Button
          text="Add to tenant"
          onClick={handleSubmit}
          disabled={!can_submit}
          loading={submitting}
        />
      </div>
    </div>
  );
};

export default AddTenantToIssueDialog;
